import { fetchRequest } from "../Fetcher";
import { pollJobUntilDone } from "./PollJobUntilDone";

const unwrap = (result) => {
  if (!result) return null;
  if (result?.data !== undefined) return result.data;
  return result;
};

const isSuccess = (result) =>
  result?.status === "success" && Number(result?.code) === 200;

const parseVideoStatus = (result) => {
  if (!result) return { state: "PENDING" };

  if (result?.status && !isSuccess(result)) {
    return { state: "FAILED", error: result?.message ?? "Request failed" };
  }

  const data = unwrap(result);
  if (!data) return { state: "PENDING" };

  const stateValue = String(
    data?.state ?? data?.status ?? data?.jobStatus ?? ""
  ).toUpperCase();

  const id = data?.id ?? data?.videoId ?? data?.video_id ?? null;
  const gcsUri =
    data?.gcsUri ??
    data?.gcs_uri ??
    data?.resultLinks?.[0] ??
    null;

  const signedUrl =
    data?.signed_url ??
    data?.signedUrl ??
    data?.url ??
    data?.video_url ??
    null;

  if (signedUrl || gcsUri) {
    return {
      state: "SUCCEEDED",
      id,
      gcsUri,
      url: signedUrl ?? null,
    };
  }

  if (["FAILED", "ERROR"].includes(stateValue)) {
    const firstError = data?.errors?.[0];
    return {
      state: "FAILED",
      id,
      error: firstError?.message ?? data?.error ?? data?.message ?? "Job failed",
      source: firstError?.source ?? null,
      suggestion: firstError?.suggestion ?? null,
    };
  }

  if (["SUCCEEDED", "COMPLETED", "DONE", "SUCCESS"].includes(stateValue)) {
    return {
      state: "SUCCEEDED",
      id,
      gcsUri,
      url: signedUrl ?? null,
    };
  }

  return { state: "PENDING", id };
};

export const runVideoGenerationJob = async ({
  storyboardId,
  sceneId,
  body,
  forceRegenerate = false,
}) => {
  const { result } = await fetchRequest({
    path: `sessions/storyboards/${storyboardId}/scenes/${sceneId}/videos/generate`,
    method: "POST",
    data: JSON.stringify({ ...body, forceRegenerate }),
  });

  const responsePayload = result?.data ?? result ?? {};
  const videoId =
    responsePayload?.videoId ?? responsePayload?.video_id ?? null;
  const gcsUri =
    responsePayload?.gcsUri ?? responsePayload?.gcs_uri ?? null;
  const signedUrl =
    responsePayload?.signed_url ??
    responsePayload?.signedUrl ??
    responsePayload?.url ??
    responsePayload?.video_url ??
    null;

  if (gcsUri || signedUrl) {
    return {
      videoId,
      gcsUri: gcsUri ?? null,
      url: signedUrl ?? null,
      state: "SUCCEEDED",
    };
  }

  if (!videoId) {
    throw new Error("Video generate response did not include videoId");
  }

  let pollError = null;
  
  try {
    await pollJobUntilDone({
      interval: 10000,
      getStatus: async () => {
        const statusResponse = await fetchRequest({
          path: `sessions/storyboards/${storyboardId}/scenes/${sceneId}/videos/${videoId}/status`,
          method: "GET",
        });

        const parsed = parseVideoStatus(statusResponse.result);

        if (parsed.state === "FAILED") {
          pollError = parsed;
        }

        return {
          result: {
            state: parsed.state,
            data: parsed,
          },
        };
      },
    });
  } catch (e) {
    if (pollError) {
      return {
        videoId: pollError.id ?? videoId,
        gcsUri: null,
        url: null,
        state: "FAILED",
        error: pollError.error ?? e.message ?? "Job failed",
        suggestion: pollError.suggestion ?? null,
      };
    }
    throw e;
  }

  const { result: finalResult } = await fetchRequest({
    path: `sessions/storyboards/${storyboardId}/scenes/videos/${videoId}`,
    method: "GET",
  });

  const parsedFinal = parseVideoStatus(finalResult);

  return {
    videoId: parsedFinal.id ?? videoId,
    gcsUri: parsedFinal.gcsUri ?? null,
    url: parsedFinal.url ?? null,
    state: parsedFinal.state,
    error: parsedFinal.error ?? null, 
    suggestion: parsedFinal.suggestion ?? null,
  };
};